import { useMutation } from "@apollo/client";
import { gql } from "@src/__generated__";
import { DocumentType } from "@src/__generated__/graphql";
import { FileWithDocType } from "@src/components/modules/Upload/useDocumentUpload";
import { useSudoziVendorMutation } from "@src/infrastructure/useSudoziMutation";
import { notifyError } from "@src/shared/notify";
import { PostVendorPortalSignedUploadUrlRoute } from "@src/sudozi-schemas/api-route-schemas";
import { splitFileNameAndExtension } from "@src/utils/string";
import { useCallback } from "react";
import { v4 } from "uuid";

export const renameFileToUuid = (file: File): File => {
  const [, extension] = splitFileNameAndExtension(file.name);
  return new File([file], `${v4()}${extension}`, { type: file.type });
};

export async function uploadToS3(
  file: FileWithDocType,
): Promise<string | void> {
  const formData = new FormData();
  formData.append("Content-Type", file.file.type);
  Object.entries(file.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file.file);

  const result = await fetch(file.url, {
    method: "POST",
    body: formData,
  }).catch((e) => {
    console.error(e);
    return { ok: false };
  });

  if (!result.ok) {
    notifyError("Error uploading file");
    return;
  }

  return file.url;
}

const CREATE_SIGNED_UPLOAD_URL = gql(`
  mutation GetSignedUploadUrl($fileName: String!) {
    signedUploadUrl(fileName: $fileName) {
      url
      fieldsJson
    }
  }
`);

export function useS3Upload() {
  const [getUploadUrl] = useMutation(CREATE_SIGNED_UPLOAD_URL);

  const addUploadUrl = useCallback(
    async (
      file: File,
      docType = [DocumentType.UNKNOWN],
    ): Promise<FileWithDocType> => {
      const res = await getUploadUrl({
        variables: {
          fileName: file.name,
        },
      });
      const url = res.data?.signedUploadUrl.url;
      const fields = JSON.parse(res.data?.signedUploadUrl.fieldsJson || "{}");
      if (!url) {
        throw new Error("A signed URL was not created");
      }
      return {
        file,
        url,
        fields,
        docType,
      };
    },
    [getUploadUrl],
  );

  const upload = useCallback(
    async (file: File, docType?: DocumentType) => {
      const renamedFile = renameFileToUuid(file);

      const fileWithUploadUrl = await addUploadUrl(renamedFile, docType);
      const response = await uploadToS3(fileWithUploadUrl);
      if (response) {
        return fileWithUploadUrl;
      }
    },
    [addUploadUrl],
  );

  return {
    upload,
  };
}

export function useVendorPortalS3Upload() {
  const getUploadUrlHandler = useSudoziVendorMutation({
    route: PostVendorPortalSignedUploadUrlRoute,
    mutationKey: ["getUploadUrl"],
  });

  return {
    upload: async (file: File) => {
      const renamedFile = renameFileToUuid(file);
      const res = await getUploadUrlHandler.mutateAsync({
        body: {
          fileName: renamedFile.name,
        },
      });

      const url = res.url;
      const fields = JSON.parse(res.fieldsJson || "{}");
      const fileWithUploadUrl = {
        file: renamedFile,
        url,
        fields,
        docType: [DocumentType.UNKNOWN],
      };

      const response = await uploadToS3(fileWithUploadUrl);

      if (response) {
        return fileWithUploadUrl;
      }
    },
  };
}
